import { initializeApp } from "firebase/app";
import {
  connectAuthEmulator,
  getAuth,
  getMultiFactorResolver,
  multiFactor,
  onIdTokenChanged,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import type { Auth, MultiFactorError, MultiFactorResolver } from "firebase/auth";
import type { FirebaseWebConfig } from "../config/env";
import type { TokenSnapshot } from "./session-mapping";

/**
 * Opaque handle linking a sent SMS code back to the flow that requested it (AUTH-007).
 * `kind: "enrollment"` — the signed-in user is adding a first second factor.
 * `kind: "challenge"` — sign-in paused because the account already has one; `resolver` finishes it.
 */
export type MfaVerification =
  | { readonly kind: "enrollment"; readonly verificationId: string }
  | {
      readonly kind: "challenge";
      readonly verificationId: string;
      /** Firebase's own masked form (e.g. "+91••••••4321"), safe to show as-is. */
      readonly maskedPhoneNumber: string | null;
      readonly resolver: MultiFactorResolver;
    };

/**
 * The web app's only doorway to Firebase Auth (AUTH-004). Components use this interface, never the
 * SDK directly, so sign-in rules (e.g. no account enumeration) live in one place and tests can
 * substitute a fake.
 */
export interface AuthClient {
  /** Called now and on every sign-in, sign-out and token refresh. Returns an unsubscribe. */
  subscribe(listener: (token: TokenSnapshot | null) => void): () => void;
  signIn(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
  sendPasswordReset(email: string): Promise<void>;
  /** Fresh ID token for API calls (refreshed by the SDK when near expiry), or null if signed out. */
  getIdToken(): Promise<string | null>;

  /** True once the signed-in user has enrolled at least one second factor (any past session). */
  hasEnrolledMfaFactor(): boolean;
  /**
   * True for the specific error `signIn()` throws when the account already has an enrolled
   * second factor: the primary sign-in paused, not failed. Pass the same error to
   * `startMfaChallenge()` to resume it.
   */
  isMfaChallenge(error: unknown): boolean;
  /**
   * Starts phone enrollment for the *signed-in* user (their first second factor). Renders an
   * invisible reCAPTCHA into `recaptchaContainerId`, which must already be in the DOM.
   */
  startMfaEnrollment(phoneNumber: string, recaptchaContainerId: string): Promise<MfaVerification>;
  /** Resumes a sign-in that `isMfaChallenge(error)` confirmed was paused for a second factor. */
  startMfaChallenge(error: unknown, recaptchaContainerId: string): Promise<MfaVerification>;
  /**
   * Confirms the code sent for either flow above. For `"enrollment"`, completes enrollment (and
   * revokes this session's refresh token, per the SDK — sign the user out and back in afterwards
   * so *this* session picks up the new second factor). For `"challenge"`, finishes the sign-in.
   */
  confirmMfaVerification(verification: MfaVerification, code: string): Promise<void>;
}

export function createFirebaseAuthClient(config: FirebaseWebConfig): AuthClient {
  const app = initializeApp({
    projectId: config.projectId,
    apiKey: config.apiKey,
    authDomain: config.authDomain,
    appId: config.appId,
  });
  const auth: Auth = getAuth(app);
  if (config.authEmulatorUrl) {
    connectAuthEmulator(auth, config.authEmulatorUrl, { disableWarnings: true });
    // The emulator never sends a real SMS regardless; this also skips loading real reCAPTCHA.
    auth.settings.appVerificationDisabledForTesting = true;
  }

  function verifier(containerId: string): RecaptchaVerifier {
    return new RecaptchaVerifier(auth, containerId, { size: "invisible" });
  }

  return {
    subscribe(listener) {
      return onIdTokenChanged(auth, (user) => {
        if (!user) {
          listener(null);
          return;
        }
        void user.getIdTokenResult().then(
          (result) => {
            listener({
              uid: user.uid,
              displayName: user.displayName,
              email: user.email,
              claims: result.claims,
            });
          },
          () => {
            listener(null); // cannot read a verified token: treat as signed out, never as signed in
          },
        );
      });
    },
    async signIn(email, password) {
      await signInWithEmailAndPassword(auth, email, password);
    },
    async signOut() {
      await signOut(auth);
    },
    async sendPasswordReset(email) {
      await sendPasswordResetEmail(auth, email);
    },
    async getIdToken() {
      return auth.currentUser ? auth.currentUser.getIdToken() : null;
    },

    hasEnrolledMfaFactor() {
      return auth.currentUser ? multiFactor(auth.currentUser).enrolledFactors.length > 0 : false;
    },

    isMfaChallenge(error) {
      return (
        typeof error === "object" &&
        error !== null &&
        (error as { code?: unknown }).code === "auth/multi-factor-auth-required"
      );
    },

    async startMfaEnrollment(phoneNumber, recaptchaContainerId) {
      const user = auth.currentUser;
      if (!user) throw new Error("startMfaEnrollment: no signed-in user");
      const session = await multiFactor(user).getSession();
      const verificationId = await new PhoneAuthProvider(auth).verifyPhoneNumber(
        { phoneNumber, session },
        verifier(recaptchaContainerId),
      );
      return { kind: "enrollment", verificationId };
    },

    async startMfaChallenge(error, recaptchaContainerId) {
      const resolver = getMultiFactorResolver(auth, error as MultiFactorError);
      const hint = resolver.hints.find((h) => h.factorId === PhoneMultiFactorGenerator.FACTOR_ID);
      if (!hint) throw new Error("startMfaChallenge: no phone second factor on this account");
      const verificationId = await new PhoneAuthProvider(auth).verifyPhoneNumber(
        { multiFactorHint: hint, session: resolver.session },
        verifier(recaptchaContainerId),
      );
      return {
        kind: "challenge",
        verificationId,
        maskedPhoneNumber: "phoneNumber" in hint ? (hint.phoneNumber as string) : null,
        resolver,
      };
    },

    async confirmMfaVerification(verification, code) {
      const credential = PhoneAuthProvider.credential(verification.verificationId, code);
      const assertion = PhoneMultiFactorGenerator.assertion(credential);
      if (verification.kind === "enrollment") {
        const user = auth.currentUser;
        if (!user) throw new Error("confirmMfaVerification: no signed-in user");
        await multiFactor(user).enroll(assertion, "Phone");
      } else {
        await verification.resolver.resolveSignIn(assertion);
      }
    },
  };
}
